<template>
  <div class="richtexting-cta-item p-3">
    <div class="field mb-3">
      <label class="block text-sm font-medium text-700 mb-2">{{ t('templateBuilder.fields.ctaText') }}</label>
      <BaseInputText
        :model-value="ctaText"
        :display-label="false"
        :max-length="ctaMaxLength || undefined"
        class="w-full"
        :class="{ 'p-invalid': validationError }"
        @update:model-value="updateCtaText"
      />
      <InlineMessage
        v-if="validationError"
        severity="error"
        class="mt-2 w-full justify-content-start"
      >
        {{ t(validationError, validationErrorParams) }}
      </InlineMessage>
    </div>
    <div class="field">
      <label
        class="block text-sm font-medium text-700 mb-2"
      >
        {{ t('templateBuilder.fields.ctaAction') }}
      </label>
      <BaseLink
        :model-value="ctaAction.value"
        :initial-link-type="ctaAction.type"
        :display-label="false"
        :link-type-options="ctaTypeOptions"
        :disabled-link-types="disabledOptions"
        :configs="configs"
        :id-campaign="idCampaign"
        :origin-call="originCall"
        :max-phone-number-length="20"
        class="w-full"
        @update:model-value="updateCtaAction"
        @update:link-type="updateCtaLinkType"
      />
    </div>
    <div
      v-if="isUrlType"
      class="field mt-3"
    >
      <label class="block text-sm font-medium text-700 mb-2">
        {{ t('templateBuilder.fields.ctaUrlDescription') }}
        <i
          v-tooltip.top="t('templateBuilder.fields.ctaUrlDescriptionTooltip')"
          class="pi pi-info-circle ml-1 text-400 cursor-pointer"
          style="font-size: 0.85rem;"
        />
      </label>
      <BaseInputText
        :model-value="ctaDescription"
        :display-label="false"
        class="w-full"
        :class="{ 'p-invalid': !ctaDescription }"
        @update:model-value="updateCtaDescription"
      />
      <small
        v-if="!ctaDescription"
        class="p-error mt-1 block"
      >
        {{ t('templateBuilder.validation.rcs.urlDescriptionRequired') }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import BaseLink from '@/components/fields/BaseLink.vue';
import InlineMessage from 'primevue/inlinemessage';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import { LinkTypeEnum } from '@/types';
import type { FieldValidator } from '@/components/template-builder/validators';

interface CtaAction {
  type: string;
  value: string;
}

export default defineComponent({
  name: 'RichTextingCtaItem',

  components: {
    BaseInputText,
    BaseLink,
    InlineMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    action: {
      type: Object as PropType<CtaAction>,
      required: true,
    },

    description: {
      type: String,
      required: false,
      default: '',
    },

    configs: {
      type: Object,
      required: true,
    },

    ctaTypeOptions: {
      type: Array as PropType<LinkTypeEnum[]>,
      required: true,
    },

    disabledOptions: {
      type: Array as PropType<LinkTypeEnum[]>,
      required: false,
      default: () => [],
    },

    idCampaign: {
      type: Number,
      required: false,
      default: null,
    },

    originCall: {
      type: String,
      required: false,
      default: null,
    },

    ctaMaxLength: {
      type: Number,
      required: false,
      default: null,
    },

    ctaValidator: {
      type: Function as PropType<FieldValidator>,
      required: false,
      default: null,
    },
  },

  emits: {
    'update:modelValue': String,
    'update:action': Object,
    'update:description': String,
  },

  setup(props, { emit }) {
    const { t } = useI18n();

    const ctaText = ref(props.modelValue);
    const ctaAction = ref<CtaAction>({ ...props.action });
    const ctaDescription = ref(props.description);
    const validationError = ref<string | null>(null);
    const validationErrorParams = ref<Record<string, string | number>>({});

    const isUrlType = computed(() => ctaAction.value.type === LinkTypeEnum.WEB_ADDRESS);

    const validateCtaText = (text: string) => {
      if (props.ctaValidator) {
        const result = props.ctaValidator(text);
        validationError.value = result.isValid ? null : (result.errorKey || null);
        validationErrorParams.value = result.errorParams || {};
      }
    };

    // Watch for external changes to props
    watch(
      () => props.modelValue,
      (newValue) => {
        ctaText.value = newValue;
        validateCtaText(newValue);
      },
    );

    watch(
      () => props.action,
      (newAction) => {
        ctaAction.value = { ...newAction };
      },
      { deep: true },
    );

    watch(
      () => props.description,
      (newDesc) => {
        ctaDescription.value = newDesc;
      },
    );

    // Validate on mount
    validateCtaText(ctaText.value);

    const updateCtaText = (newText: string) => {
      ctaText.value = newText;
      validateCtaText(newText);
      emit('update:modelValue', newText);
    };

    const updateCtaAction = (newValue: string) => {
      const updatedAction: CtaAction = {
        type: ctaAction.value.type,
        value: newValue,
      };

      ctaAction.value = updatedAction;
      emit('update:action', updatedAction);
    };

    const updateCtaLinkType = (newType: string) => {
      const updatedAction: CtaAction = {
        type: newType,
        value: ctaAction.value.value,
      };

      ctaAction.value = updatedAction;
      emit('update:action', updatedAction);
    };

    const updateCtaDescription = (newDesc: string) => {
      ctaDescription.value = newDesc;
      emit('update:description', newDesc);
    };

    return {
      t,
      ctaText,
      ctaAction,
      ctaDescription,
      isUrlType,
      validationError,
      validationErrorParams,
      updateCtaText,
      updateCtaAction,
      updateCtaLinkType,
      updateCtaDescription,
    };
  },
});
</script>

<style lang="scss" scoped>
.richtexting-cta-item {
  width: 100%;
}
</style>
